import React from 'react';
import {
  PageHeader, Table, Button, IconButton, Badge, SearchInput, FilterChip, Select,
  EmptyState, SkeletonRow, Alert, Pager,
} from '@parsina/framework';
import { UsersRound, SearchX, Plus, Stethoscope, MessagesSquare } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useShortcut } from '../../lib/shortcuts.js';
import { PracticeBanner, PracticeBadge } from '../../components/common/PracticeNotice.jsx';
import ErrorState from '../../components/common/ErrorState.jsx';
import { ageLabel, patientName } from '../../lib/caseForm.js';
import { caseStatusKey, caseStatusVariant } from '../../constants/vocabulary.js';
import usePatientsScreen from './usePatientsScreen.js';
import { CaseFormDialog, DeletePatientDialog } from './PatientDialogs.jsx';
import PatientRowActions from './PatientRowActions.jsx';
import { STATUS_CHIPS, SORTS, SORT_LABEL_KEYS, showingAll, isArchived } from './Patients.shared.js';

// «بیماران تمرینی» — the desktop list. core.md §7.3.
//
// A TABLE, not cards: the six facts a student compares across patients (name,
// age·sex, complaint, visit count, last visit, status) are one row each, and a
// card grid turns a scan down a column into a scan across a page. The phone
// gets cards for the opposite reason — a six-column table on a 360px viewport
// is a horizontal scroll.
//
// Everything below the toolbar is one of five states and never two at once:
// loading, error, no patients at all, nothing matching the filter, or the list.
// The toolbar stays usable in every one of them — an error must not take away
// the search box that would have narrowed the list that failed to load.

const TOOLBAR = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  margin: '0 0 14px',
};

export default function PatientsWeb() {
  const { t, formatNumber, formatDate } = useTranslation();
  const screen = usePatientsScreen();
  const searchRef = React.useRef(null);

  // Ctrl/⌘+K focuses the search — registered here rather than in App.jsx so it
  // binds only while this screen is mounted, which is what core.md §7.0 means
  // by "on `/patients`". It is listed in the `?` overlay because it is labelled.
  useShortcut('ctrl+k', () => searchRef.current?.focus(), {
    label: t('shortcuts.search'),
    group: t('shortcuts.groups.general'),
  });

  const columns = React.useMemo(() => [
    {
      key: 'name',
      header: t('patients.columns.name'),
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <span style={{ fontWeight: 700, color: C.textPrimary, overflowWrap: 'anywhere' }}>
            {patientName(row, t)}
          </span>
          {/* Every name, everywhere. core.md §3.1 — a patient must never read
              as a real one, on any surface, in any state. */}
          <PracticeBadge short />
          {row.caseRef && (
            <span style={{ fontSize: 11, color: CT.mute }}>{row.caseRef}</span>
          )}
        </div>
      ),
    },
    {
      key: 'age',
      header: t('patients.columns.age'),
      render: (row) => [ageLabel(row, t), row.sex ? t(`common.gender.${row.sex}`) : '']
        .filter(Boolean).join(' · ') || t('common.labels.none'),
    },
    {
      key: 'complaint',
      header: t('patients.columns.complaint'),
      render: (row) => row.chiefComplaint || t('common.labels.none'),
    },
    {
      key: 'visits',
      header: t('patients.columns.visits'),
      align: 'center',
      // The SERVER's count, never `encounters.length` — a list row carries no
      // children by contract, and `.length` there is always zero.
      render: (row) => formatNumber(Number(row.encounterCount || 0)),
    },
    {
      key: 'lastVisit',
      header: t('patients.columns.lastVisit'),
      render: (row) => (row.lastEncounterAtMs
        ? formatDate(row.lastEncounterAtMs, 'numeric')
        : t('patients.noVisits')),
    },
    {
      key: 'status',
      header: t('patients.columns.status'),
      render: (row) => (
        <Badge variant={caseStatusVariant(row.status)}>{t(caseStatusKey(row.status))}</Badge>
      ),
    },
    {
      key: 'actions',
      header: t('patients.columns.actions'),
      align: 'end',
      render: (row) => (
        <div
          style={{ display: 'inline-flex', gap: 4 }}
          // The row itself opens the record; the action buttons must not do
          // that as well on their way to doing their own job.
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            variant="ghost"
            size={34}
            icon={<Stethoscope size={16} aria-hidden />}
            label={isArchived(row)
              ? t('record.header.archivedHint')
              : t('patients.actions.startVisit')}
            disabled={isArchived(row)}
            onClick={() => screen.startVisit(row)}
            data-testid={`patient-visit-${row.id}`}
          />
          <IconButton
            variant="ghost"
            size={34}
            icon={<MessagesSquare size={16} aria-hidden />}
            label={t('patients.actions.chat')}
            onClick={() => screen.chat(row)}
            data-testid={`patient-chat-${row.id}`}
          />
          <PatientRowActions
            row={row}
            onOpen={screen.open}
            onArchive={screen.archive}
            onDelete={screen.setDeleteTarget}
          />
        </div>
      ),
    },
  ], [t, formatNumber, formatDate, screen]);

  return (
    <div style={{ padding: '0 0 32px' }} data-testid="patients-web">
      <PageHeader title={t('patients.title')} sub={t('patients.sub')}>
        <Button onClick={screen.openCreate} data-testid="patients-new">
          <Plus size={15} aria-hidden />
          {t('patients.new')}
        </Button>
      </PageHeader>

      <div style={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <PracticeBanner />

        <div style={TOOLBAR}>
          <SearchInput
            ref={searchRef}
            value={screen.query}
            onChange={(e) => screen.setQuery(e.target.value)}
            placeholder={t('patients.search')}
            aria-label={t('patients.search')}
            clearLabel={t('common.actions.clear')}
            style={{ flex: '1 1 240px', maxWidth: 340 }}
            data-testid="patients-search"
          />

          {STATUS_CHIPS.map((chip) => (
            <FilterChip
              key={chip.id}
              label={t(chip.labelKey)}
              active={(screen.statuses || []).includes(chip.id)}
              onClick={() => screen.toggleStatusChip(chip.id)}
              data-testid={`patients-filter-${chip.id}`}
            />
          ))}
          <FilterChip
            label={t('patients.filters.all')}
            active={showingAll(screen.statuses)}
            onClick={screen.showAll}
            data-testid="patients-filter-all"
          />

          <Select
            value={screen.sort}
            onChange={(e) => screen.setSort(e.target.value)}
            options={SORTS.map((id) => ({ value: id, label: t(SORT_LABEL_KEYS[id]) }))}
            placeholder=""
            aria-label={t('patients.sort.label')}
            style={{ marginInlineStart: 'auto', minWidth: 160 }}
            data-testid="patients-sort"
          />
        </div>

        {/* The server capped the answer at 100 rows. Saying so is the honest
            move: there is no `total` on this query to page against, and the
            recovery — search — is the one the shape can actually support. */}
        {screen.capped && !screen.loading && (
          <Alert variant="info" data-testid="patients-capped">{t('patients.capped')}</Alert>
        )}

        {screen.loading && (
          <div data-testid="patients-loading">
            {[0, 1, 2].map((i) => <SkeletonRow key={i} />)}
          </div>
        )}

        {!screen.loading && screen.error && (
          <ErrorState
            error={screen.error}
            title={t('patients.error.title')}
            description={t('patients.error.body')}
            onRetry={screen.reload}
            data-testid="patients-error"
          />
        )}

        {/* «هنوز بیماری نساخته‌اید» ONLY when there is nothing in ANY status:
            the status chips are a wire filter, and «بایگانی» with no archived
            rows is the filter's emptiness, not the student's. The hook has
            already asked the unfiltered question (core.md §7.3). */}
        {screen.trulyEmpty && (
          <EmptyState
            icon={<UsersRound size={44} strokeWidth={1.5} aria-hidden />}
            title={t('patients.empty.title')}
            description={t('patients.empty.body')}
            action={
              <Button onClick={screen.openCreate} data-testid="patients-empty-new">
                {t('patients.new')}
              </Button>
            }
            data-testid="patients-empty"
          />
        )}

        {/* A filtered-empty list is NOT an empty one: the student has patients,
            they just typed something none of them match — or picked a chip
            none of them carry. Different icon, different sentence, different
            recovery (core.md §7.3). */}
        {screen.filteredEmpty && (
          <EmptyState
            icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
            title={t('patients.noMatch.title')}
            description={t('patients.noMatch.body')}
            action={
              <Button variant="outline" onClick={screen.clearFilters} data-testid="patients-clear-filter">
                {t('patients.actions.clearFilters')}
              </Button>
            }
            data-testid="patients-no-match"
          />
        )}

        {!screen.loading && !screen.error && screen.visible.length > 0 && (
          <>
            <Table
              columns={columns}
              rows={screen.pageRows}
              rowKey={(row) => row.id}
              onRowActivate={screen.open}
              rowProps={(row) => ({
                'data-testid': `patient-row-${row.id}`,
                'aria-label': t('patients.openLabel', { title: patientName(row, t) }),
              })}
              data-testid="patients-table"
            />
            {/* Client-side pages of 25 over the ≤100 rows held (lib/paging.js).
                The kit's Pager defaults to Persian copy and fa-IR digits; both
                come from the catalogue here so an English panel does not carry
                a «صفحه ۱ از ۴». Renders nothing for a single page. */}
            <div data-testid="patients-pager">
              <Pager
                page={screen.page}
                pageCount={screen.pages}
                onChange={screen.setPage}
                prevLabel={t('common.pager.prev')}
                nextLabel={t('common.pager.next')}
                pageLabel={(current, pages) => t('common.pager.page', { current, pages })}
                formatNumber={formatNumber}
              />
            </div>
          </>
        )}
      </div>

      <CaseFormDialog
        open={screen.creating}
        onClose={screen.closeCreate}
        values={screen.form}
        onChange={screen.setForm}
        onSubmit={screen.submitCreate}
        saving={screen.saving}
        showTitleError={screen.showTitleError}
        mode="create"
      />
      <DeletePatientDialog
        row={screen.deleteTarget}
        onCancel={() => screen.setDeleteTarget(null)}
        onConfirm={screen.confirmDelete}
        loading={screen.deleting}
      />
    </div>
  );
}
